import { t } from '@/utils/i18n'
import { useState } from 'react'
import { useSetState } from 'ahooks'
import { Button, Toast, InfiniteScroll, ErrorBlock, NavBar } from 'antd-mobile'
import { useNavigate, useLocation } from 'react-router-dom'
import styles from './index.module.less'
import PickTypeLabel from '@/components/PickTypeLabel'
import { useCorpSettingStore } from '@/stores'
import ShippingInfo from '@/components/ShippingInfo/ShippingInfo'
import { constant, utils, cal } from '@/utils'
import type { OrderPickType } from '@/pages/Order/types'
import eventUtil from '@/utils/event'
import useGetLogistics from '@/pages/Order/hooks/useGetLogistics'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'

const SHOW_COD_FEE = false;
const { to, isExist } = utils
const { moneyFormat } = cal
const {
  order: { PickType },
} = constant

function ShippingTemplate() {
  const location = useLocation()
  const { corpSetting, setCorpSetting } = useCorpSettingStore()
  const { currencySymbol, language } = corpSetting
  const locationState = location?.state || {}
  const { logisticsId, deliveryType, crossBorder } = locationState
  const navigate = useNavigate()
  const [templateList, setTemplateList] = useState<OrderPickType[]>([])
  const [pageInfo, setPageInfo] = useSetState({
    pageNum: 0,
    pageSize: 50,
    hasNextPage: true,
    isDataEmpty: false,
  })
  const { getLogisticsList: toGetLogisticsList } = useGetLogistics()
  const langStr = language.indexOf('zh') > -1 ? language.toLocaleLowerCase() : language.split('-')[0]
  let dateFormat = 'Do MMM' // 默认英式格式
  dayjs.locale(langStr)
  if (langStr.indexOf('zh') > -1) {
    dateFormat = 'M月 D日' // 中式格式
  } else {
    dayjs.extend(advancedFormat)
  }

  // 获取物流列表
  async function getLogisticsList(_pageNum?: number) {
    const { pageSize } = pageInfo
    const pageNum = _pageNum || pageInfo.pageNum
    const [err, res] = await to(
      toGetLogisticsList({
        pageNum,
        pageSize,
        crossBorder,
      })
    )

    if (err || !res) {
      return
    }

    const handleData: OrderPickType[] = res.data || []
    let isDataEmpty = false
    const { pages, extend = {} } = res
    const hasNextPage = pageNum < pages
    let newTemplateList = handleData

    if (pageNum === 1) {
      setTemplateList(newTemplateList)
      if (!handleData) isDataEmpty = true
    } else {
      newTemplateList = [...templateList, ...handleData]
      setTemplateList(newTemplateList)
    }
    setPageInfo({
      hasNextPage,
      isDataEmpty,
    })

    setCorpSetting({
      ...corpSetting,
      cod: Object.assign({}, corpSetting.cod, {
        codMin: extend.codMin,
        codMax: extend.codMax,
      })
    })
    handleSelectedTemp({ logisticsId } as unknown as OrderPickType, newTemplateList)
  }

  // 处理选中的物流
  function handleSelectedTemp(data: OrderPickType, _templateList: OrderPickType[] = templateList) {
    // 判断订单金额是否在cod范围内
    if (isExist(data.pickType) && data.pickType === PickType.COD && data.confStatus === 0) {
      Toast.show(t('订单金额不在COD范围内'))
      return
    }

    _templateList.forEach(temp => {
      temp.checked = temp.logisticsId === data.logisticsId
    })
    setTemplateList([..._templateList])
  }

  // 加载更多列表
  async function loadNextPage() {
    if (!pageInfo.hasNextPage) return

    const pageNum = pageInfo.pageNum + 1
    setPageInfo({
      pageNum,
    })
    return getLogisticsList(pageNum)
  }
  
  // 获取底部的显示信息（地址、运费、COD服务费）
  function getFooterLabel(data: OrderPickType) {
    const { pickType, shipAddress, shippingFee, codFee } = data
    return (
      <div className={`${styles['text-item']} ep-2`}>
        { pickType === PickType.SELF_PICK && (
          <>{t('地址:')}{shipAddress}</>
        ) }
        { SHOW_COD_FEE && pickType !== PickType.SELF_PICK && (
          <>
            <span>
              {t('运输费')} {currencySymbol} {moneyFormat(shippingFee)}
            </span>{' '}
            +{' '}
            <span>
              {t('COD服务费')} {currencySymbol} {moneyFormat(codFee)}
            </span>
          </>
        ) }
      </div>
    )
  }

  // 模板选择取消
  function onTempCancel() {
    const defaultTempData = templateList.find(temp => temp.logisticsId === logisticsId)
    eventUtil.emit('shippingOptionChange', { shippingOption: defaultTempData, deliveryType })
    navigate(-1)
  }

  // 模板选择确定
  function onTempConfirm() {
    const selectedTempData = templateList.find(temp => temp.checked)
    eventUtil.emit('shippingOptionChange', { shippingOption: selectedTempData, deliveryType })
    navigate(-1)
  }

  function createdExpectTimeLabel(
    expectTimeStart: string,
    expectTimeEnd: string,
    expectTimeRemark: string = ''
  ) {
    if (!expectTimeStart && !expectTimeEnd) return '';
  
    const format = dateFormat;
    const start = dayjs(Number(expectTimeStart)).format(format);
    const end = dayjs(Number(expectTimeEnd)).format(format);
    const timeLabel = `${t("预计送达")} ${start}${expectTimeStart !== expectTimeEnd ? ` ~ ${end}` : ''}${
      expectTimeRemark ? `(${expectTimeRemark})` : ''
    }`;
  
    return <div className={`${styles['text-item']} ${styles['time']}`}>{timeLabel}</div>;
  }
  
  function templateItemStyles(item:any) {
    if (item.checked) {
      return {
        borderColor: item.pickType === PickType.COD ? '#26c25f' : '#4080FF',
      }
    }

    return {}
  }

  return (
    <div className={styles['shipping-template']}>
      <NavBar className={styles['nav-bar']}
        children={t('物流选项')} onBack={ onTempCancel } />

      <div className={styles['template-list']}>
        {!!templateList.length &&
          templateList.map((item, index) => (
            <div key={index}
              className={`
                ${styles['template-item']}
                ${ item.checked ? styles['checked'] : '' }
                ${ item.confStatus === 0 && item.pickType === PickType.COD ? styles['disabled'] : '' }
              `}
              style={templateItemStyles(item)}
              onClick={ () => handleSelectedTemp(item) }>
              <div className={styles['template-top']}>
                <PickTypeLabel pickType={item.pickType} shippingType={item.shippingType} crossBorder={item.crossBorder} />
                <div className={`${styles['template-name']} ep`}>
                  {item.logisticsName}
                </div>
              </div>
              {SHOW_COD_FEE &&<div className={styles['shipping-total']}>
                {currencySymbol} {moneyFormat(item.shippingTotal || 0)}
              </div>}
              <div className={styles['template-footer']}>
                <div className={styles['text-wrap']}>
                  { getFooterLabel(item) }
                  {createdExpectTimeLabel(item.expectTimeStart, item.expectTimeEnd, item.expectTimeRemark)}
                </div>
              </div>
            </div>
          ))
        }

        {!pageInfo.isDataEmpty && pageInfo.hasNextPage && (
          <InfiniteScroll
            hasMore={pageInfo.hasNextPage}
            finished-text={t('没有更多了')}
            loadMore={loadNextPage}
          />
        )}

        {pageInfo.isDataEmpty && (
          <ErrorBlock
            status="empty"
            title={t('暂无物流数据')}
            description=""
          />
        )}
        <div className={styles['shippingInfo-wrap']}>
          <ShippingInfo deliveryType={deliveryType || 0} />
        </div>
        <div className={styles["card-wrap"]}>
          <h2 className={styles["title"]}>{t("大额订单说明")}</h2>
          <div className={styles["desc"]}>{t("订单超过1亿，店长会主动联系您协助完成交易")}</div>
        </div>
      </div>
      <div className={styles['footer-box']}>
        <Button color='primary'
          className={styles['confirm-btn']}
          onClick={ onTempConfirm }>{t('确定')}</Button>
      </div>
    </div>
  )
}

export default ShippingTemplate
